import { initChunk, complete } from '../api/upload/api.js'
let fileId = ''
/**
 * 文件分片上传工具类
 * 支持大文件分片上传、断点续传、进度跟踪、失败重试等功能
 */
class ChunkUploader {
  constructor(options = {}) {
    // 默认配置
    this.defaultOptions = {
      chunkSize: 5 * 1024 * 1024, // 默认分片大小 5MB
      maxConcurrent: 3, // 最大并发上传数
      maxRetries: 3, // 最大重试次数
      retryDelay: 1000, // 重试延迟时间(ms)
      
      uploadUrl: '/x-file/multipart/complete', // 分片上传接口
      initUrl: '/x-file/upload/multipart/init', // 分片初始化接口
      timeout: 30000, // 单个分片上传超时时间
      business: 'default', // 业务标识
      function: 'upload', // 功能标识
      ...options
    }
    // 上传状态管理
    this.uploadTasks = new Map() // 存储所有上传任务
    this.activeUploads = new Map() // 存储正在进行的上传
  }
  /**
   * 计算文件MD5值（用于文件唯一标识）
   */
  async calculateFileMD5(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result
          const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer)
          const hashArray = Array.from(new Uint8Array(hashBuffer))
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
          resolve(hashHex)
        } catch (error) {
          reject(error)
        }
      }
      reader.onerror = reject
      reader.readAsArrayBuffer(file)
    })
  }
  /**
   * 将文件分割成多个分片
   */
  splitFileIntoChunks(file, chunkSize = this.defaultOptions.chunkSize) {
    const chunks = []
    let start = 0
    let index = 0
    while (start < file.size) {
      const end = Math.min(start + chunkSize, file.size)
      const chunk = file.slice(start, end)
  
      chunks.push({
        index,
        start,
        end,
        size: chunk.size,
        blob: chunk,
        uploaded: false,
        retryCount: 0
      })
      
      start = end
      index++
    }

    return chunks
  }

  /**
   * 初始化分片上传
   */
  async initChunkUpload(fileName, fileSize, partCount, projectCode = '', contentType = '', fileBindId, fileType, secretKey) {
    try {
      const payload = {
        fileName: fileName,
        fileSize: fileSize,
        partCount: partCount,
        projectCode: projectCode,
        business: this.defaultOptions.business,
        function: this.defaultOptions.function,
        fileBindId: fileBindId,
        fileType: fileType
      }
      if (secretKey) payload.secretKey = secretKey
      const response = await initChunk(payload)
      const result = response.data || response
      // 直接返回result，不要处理uploadUrls
      fileId = result.fileId

      let uploadUrls = result.uploadUrls || []
      // 如果 uploadUrls 是对象而不是数组，转换为数组
      if (uploadUrls && typeof uploadUrls === 'object' && !Array.isArray(uploadUrls)) {
        uploadUrls = Object.values(uploadUrls)
      }
      
      // 如果 uploadUrls 是包含 uploadUrl 字段的对象数组，提取 URL
      if (uploadUrls && Array.isArray(uploadUrls) && uploadUrls.length > 0) {
        if (uploadUrls[0] && typeof uploadUrls[0] === 'object' && uploadUrls[0].uploadUrl) {
          uploadUrls = uploadUrls.map(part => part.uploadUrl)
        } else if (uploadUrls[0] && typeof uploadUrls[0] === 'object' && uploadUrls[0].url) {
          uploadUrls = uploadUrls.map(part => part.url)
        }
      }
      // 将处理后的 uploadUrls 添加到结果中
      result.uploadUrls = uploadUrls
      return result;
    } catch (error) {
      throw new Error(`分片初始化失败: ${error.message}`)
    }
  }

  /**
   * 上传单个分片
   * @param {Object} chunk 分片对象
   * @param {string} uploadId 上传ID
   * @param {string} fileName 文件名
   * @param {string} uploadUrl 分片上传的预签名URL
   * @returns {Promise<Object>} 上传结果
   */
  async uploadChunk(chunk, uploadId, fileName, uploadUrl) {
    try {
      // 使用预签名URL直接上传到S3
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: chunk.blob,
        headers: {
          'Content-Type': chunk.blob.type || 'application/octet-stream'
        }
      })

      if (!response.ok) {
        throw new Error(`分片上传失败: ${response.status} ${response.statusText}`)
      }

      // 获取ETag，这是S3分片上传需要的
      const etag = response.headers.get('ETag')
      
      // 将ETag保存到分片对象中
      chunk.uploaded = true
      chunk.etag = etag
      
      return {
        partNumber: chunk.index,
        etag: etag,
        success: true
      }
    } catch (error) {
      chunk.retryCount++
      throw error
    }
  }

  /**
   * 合并分片
   * @param {string} uploadId 上传ID
   * @param {string} fileName 文件名
   * @param {string} fileUrl 文件URL（来自分片初始化的mergeUploadUrl）
   * @param {number} totalChunks 总分片数
   * @param {Array} chunks 分片数组（用于获取ETag信息）
   * @returns {Promise<Object>} 合并结果
   */
  async mergeChunks(uploadId, fileName, fileUrl = '', totalChunks, chunks = [], secretKey = '') {
    try {
      const completeData = {
        uploadId,
        fileName,
        fileUrl
      }
      if (secretKey) completeData.secretKey = secretKey
      
      console.log('调用分片合并接口:', {
        url: '/api/upload/multipart/complete',
        data: completeData,
        uploadId,
        fileName,
        fileUrl
      })
      
      let response = await complete(completeData)
      response.fileId = fileId
      return response.data || response
    } catch (error) {
      console.error('分片合并失败详情:', {
        error: error.message,
        uploadId,
        fileName,
        fileUrl,
        response: error.response?.data
      })
      throw new Error(`分片合并失败: ${error.message}`)
    }
  }

  /**
   * 执行分片上传
   * @param {File} file 文件对象
   * @param {Object} options 上传选项
   * @returns {Promise<Object>} 上传结果
   */
  async uploadFile(file, options = {}) {
    const uploadOptions = { ...this.defaultOptions, ...options }
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const chunks = this.splitFileIntoChunks(file, uploadOptions.chunkSize)
    // 新增 fileBindId 透传
    const fileBindId = uploadOptions.fileBindId ;
    const initResult = await this.initChunkUpload(
      file.name,
      file.size,
      chunks.length,
      uploadOptions.projectCode || file.projectCode || '',
      file.type,
      fileBindId,
      uploadOptions.fileType || file.fileType, // 传给initChunkUpload
      uploadOptions.secretKey
    )
    // 从初始化结果中获取uploadId（如果后端返回的话）
    const finalUploadId = initResult.uploadId || uploadId
    // 创建上传任务
    const uploadTask = {
      id: finalUploadId,
      file,
      fileName: file.name,
      projectCode: file.projectCode,
      chunks,
      totalChunks: chunks.length,
      uploadedChunks: 0,
      failedChunks: [],
      progress: 0,
      status: 'uploading', // uploading, completed, failed, paused
      startTime: Date.now(),
      onProgress: options.onProgress,
      onSuccess: options.onSuccess,
      onError: options.onError,
      initResult,
      uploadUrls: initResult.uploadUrls || [] // 添加预签名URL数组
    }

    this.uploadTasks.set(finalUploadId, uploadTask)
    try {
      // 并发上传分片
      await this.uploadChunksConcurrently(uploadTask, uploadOptions)
      // 合并分片
      const mergeResult = await this.mergeChunks(finalUploadId, file.name, initResult.mergeUploadUrl || '', chunks.length, chunks, uploadOptions.secretKey)
      
      uploadTask.status = 'completed'
      uploadTask.progress = 100
      
      if (uploadTask.onProgress) {
        uploadTask.onProgress(100, uploadTask)
      }
      
      if (uploadTask.onSuccess) {
        uploadTask.onSuccess(mergeResult, uploadTask)
      }

      return {
        fileId: fileId,
        success: true,
        uploadId: finalUploadId,
        fileUrl: mergeResult.fileUrl || mergeResult.url,
        message: '文件上传成功',
        data: mergeResult
      }

    } catch (error) {
      uploadTask.status = 'failed'
      
      if (uploadTask.onError) {
        uploadTask.onError(error, uploadTask)
      }
      throw error
    } finally {
      // 清理任务
      this.uploadTasks.delete(finalUploadId)
    }
  }

  /**
   * 并发上传分片
   * @param {Object} uploadTask 上传任务
   * @param {Object} options 上传选项
   */
  async uploadChunksConcurrently(uploadTask, options) {
    const { chunks, fileName, id: uploadId, uploadUrls } = uploadTask
    const { maxConcurrent, maxRetries } = options
    // 过滤未上传的分片
    const pendingChunks = chunks.filter(chunk => !chunk.uploaded)
    // 创建上传队列
    const uploadQueue = [...pendingChunks]
    const activeUploads = new Set()
    
    return new Promise((resolve, reject) => {
      let hasError = false
      
      const processNextChunk = async () => {
        if (hasError || uploadQueue.length === 0) {
          if (activeUploads.size === 0) {
            if (hasError) {
              reject(new Error('分片上传失败'))
            } else {
              resolve()
            }
          }
          return
        }

        if (activeUploads.size >= maxConcurrent) {
          return
        }

        const chunk = uploadQueue.shift()
        if (!chunk) return

        const uploadPromise = this.uploadChunkWithRetry(chunk, uploadId, fileName, uploadUrls, maxRetries)
        activeUploads.add(uploadPromise)

        try {
          await uploadPromise
          uploadTask.uploadedChunks++
          const uploadUrlsLength = uploadTask.uploadUrls?.length || 0
          const currentChunkIndex = chunk.index
          uploadTask.currentChunkIndex = currentChunkIndex
          uploadTask.progress = uploadUrlsLength > 0 
            ? Math.round(((currentChunkIndex + 1) / uploadUrlsLength) * 100)
            : Math.round((uploadTask.uploadedChunks / uploadTask.totalChunks) * 100)
          if (uploadTask.onProgress) {
            uploadTask.onProgress(uploadTask.progress, uploadTask)
          }

        } catch (error) {
          uploadTask.failedChunks.push(chunk)
          hasError = true
        } finally {
          activeUploads.delete(uploadPromise)
          // 继续处理下一个分片
          setTimeout(processNextChunk, 0)
        }
      }

      // 启动并发上传
      for (let i = 0; i < maxConcurrent; i++) {
        processNextChunk()
      }
    })
  }

  /**
   * 带重试机制的分片上传
   * @param {Object} chunk 分片对象
   * @param {string} uploadId 上传ID
   * @param {string} fileName 文件名
   * @param {Array} uploadUrls 分片上传URL数组
   * @param {number} maxRetries 最大重试次数
   * @returns {Promise<Object>} 上传结果
   */
  async uploadChunkWithRetry(chunk, uploadId, fileName, uploadUrls, maxRetries) {
    let lastError
    
    console.log(`开始上传分片 ${chunk.index}:`, {
      chunkIndex: chunk.index,
      uploadUrlsLength: uploadUrls ? uploadUrls.length : 'undefined',
      uploadUrls: uploadUrls,
      targetUrlIndex: chunk.index,
      targetUrl: uploadUrls ? uploadUrls[chunk.index] : 'undefined'
    })
    
    // 检查 uploadUrls 是否存在且为数组
    if (!uploadUrls || !Array.isArray(uploadUrls)) {
      console.error('uploadUrls 不存在或不是数组:', uploadUrls)
      throw new Error('uploadUrls 不存在或格式错误')
    }
    
    // 检查分片索引是否在有效范围内 (支持从0开始的索引)
    if (chunk.index < 0 || chunk.index >= uploadUrls.length) {
      console.error(`分片索引 ${chunk.index} 超出范围:`, {
        chunkIndex: chunk.index,
        uploadUrlsLength: uploadUrls.length,
        validRange: `0-${uploadUrls.length - 1}`
      })
      throw new Error(`分片索引 ${chunk.index} 超出有效范围 (0-${uploadUrls.length - 1})`)
    }
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // 获取对应分片的预签名URL (分片索引从0开始，直接使用chunk.index)
        const uploadUrl = uploadUrls[chunk.index]
        if (!uploadUrl) {
          console.error(`分片 ${chunk.index} 的预签名URL不存在:`, {
            chunkIndex: chunk.index,
            uploadUrlsLength: uploadUrls.length,
            uploadUrls: uploadUrls,
            targetUrlIndex: chunk.index,
            targetUrl: uploadUrl
          })
          throw new Error(`分片 ${chunk.index} 的预签名URL不存在`)
        }
        
        return await this.uploadChunk(chunk, uploadId, fileName, uploadUrl)
      } catch (error) {
        lastError = error
        
        if (attempt < maxRetries) {
          // 等待后重试
          await this.delay(this.defaultOptions.retryDelay * Math.pow(2, attempt))
        }
      }
    }
    
    throw lastError
  }

  /**
   * 延迟函数
   * @param {number} ms 延迟时间(毫秒)
   * @returns {Promise} Promise对象
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * 暂停上传
   * @param {string} uploadId 上传ID
   */
  pauseUpload(uploadId) {
    const task = this.uploadTasks.get(uploadId)
    if (task) {
      task.status = 'paused'
    }
  }

  /**
   * 恢复上传
   * @param {string} uploadId 上传ID
   */
  resumeUpload(uploadId) {
    const task = this.uploadTasks.get(uploadId)
    if (task && task.status === 'paused') {
      task.status = 'uploading'
      // 重新开始上传未完成的分片
      this.uploadFile(task.file, {
        onProgress: task.onProgress,
        onSuccess: task.onSuccess,
        onError: task.onError
      })
    }
  }

  /**
   * 取消上传
   * @param {string} uploadId 上传ID
   */
  cancelUpload(uploadId) {
    const task = this.uploadTasks.get(uploadId)
    if (task) {
      task.status = 'cancelled'
      this.uploadTasks.delete(uploadId)
    }
  }

  /**
   * 获取上传进度
   * @param {string} uploadId 上传ID
   * @returns {Object} 进度信息
   */
  getUploadProgress(uploadId) {
    const task = this.uploadTasks.get(uploadId)
    if (!task) {
      return null
    }

    return {
      uploadId,
      progress: task.progress,
      status: task.status,
      uploadedChunks: task.uploadedChunks,
      totalChunks: task.totalChunks,
      failedChunks: task.failedChunks.length,
      fileName: task.fileName,
      fileSize: task.file.size,
      startTime: task.startTime,
      elapsedTime: Date.now() - task.startTime
    }
  }

  /**
   * 获取所有上传任务
   * @returns {Array} 所有任务列表
   */
  getAllUploadTasks() {
    return Array.from(this.uploadTasks.values())
  }

  /**
   * 清理所有任务
   */
  clearAllTasks() {
    this.uploadTasks.clear()
    this.activeUploads.clear()
  }
}

// 创建单例实例
const chunkUploader = new ChunkUploader()

// 导出实例和类
export default chunkUploader
export { ChunkUploader }

// 便捷方法
export const uploadFile = (file, options) => chunkUploader.uploadFile(file, options)
export const pauseUpload = (uploadId) => chunkUploader.pauseUpload(uploadId)
export const resumeUpload = (uploadId) => chunkUploader.resumeUpload(uploadId)
export const cancelUpload = (uploadId) => chunkUploader.cancelUpload(uploadId)
export const getUploadProgress = (uploadId) => chunkUploader.getUploadProgress(uploadId)
export const getAllUploadTasks = () => chunkUploader.getAllUploadTasks()
export const clearAllTasks = () => chunkUploader.clearAllTasks()
